import { apiPost } from '@/lib/http/client';

export interface ResolvedSlot {
  id: string;
  start: string;
  end: string;
  /**
   * Server-rendered human label ("Thursday, August 20 at 9:00 AM PDT"),
   * rendered in the requesting user's profile timezone by
   * `apps.utils.slot_time_rendering.render_slot_time` — the SINGLE source of
   * the slot time format. Display it verbatim; never re-format client-side.
   */
  label: string;
}

interface SlotResolveResponse {
  slots: ResolvedSlot[];
}

// The slot resolve endpoint caps slot_ids at 100 per request
// (apps/scheduling/serializers.py SlotResolveRequestSerializer). Chunk to this
// size so a request with more than 100 distinct slot ids still resolves every
// slot instead of failing the whole batch.
export const SLOT_RESOLVE_BATCH_SIZE = 100;

/**
 * Resolves scheduling slot ids to their start/end times via
 * `POST /api/orgs/:orgId/scheduling/slots/resolve/`, chunked to the
 * endpoint's per-request cap.
 *
 * The resolver keys slots by their canonical lowercase UUID (DRF UUIDField
 * serializes lowercase), so slot ids are lowercased before being sent —
 * an uppercase-cased reference still resolves instead of silently missing.
 * Each returned slot's `id` is mapped back to the ORIGINAL input casing, so an
 * exact-match consumer (`slot.id === inputId`, e.g. the editor's node predicate
 * and MeetingSlotBadge) still matches an id that was supplied uppercase — it no
 * longer sees a lowercased id it would treat as unresolved.
 *
 * `onChunkStart`, if given, fires with each chunk's ids right before that
 * chunk's request is sent — never for a later chunk that's never reached
 * because an earlier one rejected. Callers that need to know exactly which
 * ids were actually attempted (as opposed to merely requested) use this
 * instead of assuming every id in `slotIds` was sent.
 *
 * `onChunkResolved`, if given, fires with each chunk's resolved slots as soon
 * as that chunk's request succeeds — BEFORE any later chunk is attempted. A
 * caller can apply successes incrementally so that a later chunk's rejection
 * (which rejects this whole promise, discarding the accumulated return value)
 * does not cost it the earlier chunks' already-resolved slots.
 */
export async function resolveSlots(
  orgId: number,
  slotIds: string[],
  onChunkStart?: (chunkIds: string[]) => void,
  onChunkResolved?: (chunkSlots: ResolvedSlot[]) => void
): Promise<ResolvedSlot[]> {
  const resolved: ResolvedSlot[] = [];
  for (let i = 0; i < slotIds.length; i += SLOT_RESOLVE_BATCH_SIZE) {
    const chunk = slotIds.slice(i, i + SLOT_RESOLVE_BATCH_SIZE);
    onChunkStart?.(chunk);
    const lowerToInput = new Map(chunk.map(id => [id.toLowerCase(), id]));
    const response = await apiPost<SlotResolveResponse>(
      `/api/orgs/${orgId}/scheduling/slots/resolve/`,
      { slot_ids: chunk.map(id => id.toLowerCase()) }
    );
    // Restore the caller's original id casing so exact-match consumers still
    // match; fall back to the endpoint's id if it doesn't map to an input.
    const chunkSlots = response.slots.map(slot => ({
      ...slot,
      id: lowerToInput.get(slot.id.toLowerCase()) ?? slot.id,
    }));
    resolved.push(...chunkSlots);
    onChunkResolved?.(chunkSlots);
  }
  return resolved;
}
